import React, { useState, useEffect } from 'react';
import { caseService, clientService, userService, conflictService, searchClientsAsync } from '../services/api';
import SearchableSelect from './common/SearchableSelect';
import AsyncSearchableSelect from './common/AsyncSearchableSelect';
import ConflictWarningDialog from './conflicts/ConflictWarningDialog';
import {
  X,
  CheckCircle2,
  AlertCircle,
  Briefcase
} from 'lucide-react';
import useModal from '../hooks/useModal';

const SimpleCaseModal = ({ isOpen, onClose, onSuccess, preSelectedClientId = null }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attorneys, setAttorneys] = useState([]);
  const [preSelectedClientName, setPreSelectedClientName] = useState(null);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [pendingCaseData, setPendingCaseData] = useState(null);

  // Modal behavior
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);

  // Form data
  const [caseForm, setCaseForm] = useState({
    case_number: '',
    title: '',
    case_type: '',
    description: '',
    client_id: preSelectedClientId || '',
    assigned_attorney_id: '',
    status: 'active',
    total_estimated_value: '',
    billing_type: 'hourly',
    flat_fee_amount: '',
    contingency_percentage: '',
    requires_trust_funds: true,
    trust_minimum_balance: '',
  });

  useEffect(() => {
    if (isOpen) {
      fetchAttorneys();
      if (preSelectedClientId) {
        setCaseForm(prev => ({ ...prev, client_id: preSelectedClientId }));
      }
    }
  }, [isOpen, preSelectedClientId]);

  useEffect(() => {
    if (preSelectedClientId) {
      clientService.getClient(preSelectedClientId).then(res => {
        const c = res.data;
        setPreSelectedClientName([c.first_name, c.last_name].filter(Boolean).join(' '));
      }).catch(() => {});
    }
  }, [preSelectedClientId]);

  const fetchAttorneys = async () => {
    setLoading(true);
    // Fetch attorneys separately so a missing view_users permission doesn't break the whole form
    try {
      const attorneysResponse = await userService.getUsers(false, 'Attorney');
      const activeAttorneys = (attorneysResponse.data || []).filter(u => u.firm_active !== false && !u.has_pending_invitation);
      setAttorneys(activeAttorneys);
    } catch (error) {
      console.error('Could not load attorneys:', error);
      setAttorneys([]);
    }
    setLoading(false);
  };

  const createCaseAfterCheck = async (caseData) => {
    const response = await caseService.createCase(caseData);
    if (onSuccess) {
      onSuccess(response.data);
    }
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Prepare data for API
      const caseData = {
        case_number: caseForm.case_number || undefined,
        title: caseForm.title,
        case_type: caseForm.case_type,
        description: caseForm.description || undefined,
        client_id: caseForm.client_id,
        assigned_attorney_id: caseForm.assigned_attorney_id || undefined,
        status: caseForm.status,
        total_estimated_value: caseForm.total_estimated_value ? parseFloat(caseForm.total_estimated_value) : undefined,
        billing_type: caseForm.billing_type,
        flat_fee_amount: caseForm.flat_fee_amount ? parseFloat(caseForm.flat_fee_amount) : undefined,
        contingency_percentage: caseForm.contingency_percentage ? parseFloat(caseForm.contingency_percentage) : undefined,
        requires_trust_funds: caseForm.requires_trust_funds,
        trust_minimum_balance: caseForm.trust_minimum_balance ? parseFloat(caseForm.trust_minimum_balance) : undefined,
      };

      // Run conflict check on selected client before creating case
      if (caseForm.client_id) {
        try {
          const clientRes = await clientService.getClient(caseForm.client_id);
          const selectedClient = clientRes.data;
          if (selectedClient) {
            try {
              const checkResponse = await conflictService.checkConflicts({
                first_name: selectedClient.first_name,
                last_name: selectedClient.last_name,
                company_name: selectedClient.company_name,
                email: selectedClient.email,
                phone: selectedClient.phone,
                check_type: 'case_creation',
              });
              if (checkResponse.data.blocking) {
                setConflictData({
                  conflictCheckId: checkResponse.data.conflict_check_id,
                  matches: checkResponse.data.matches || [],
                });
                setPendingCaseData(caseData);
                setShowConflictDialog(true);
                setLoading(false);
                return;
              }
            } catch (conflictErr) {
              // If conflict check fails, proceed with case creation
              console.error('Conflict check failed, proceeding:', conflictErr);
            }
          }
        } catch (clientErr) {
          // If client fetch fails, proceed with case creation
          console.error('Client fetch for conflict check failed, proceeding:', clientErr);
        }
      }

      await createCaseAfterCheck(caseData);
    } catch (error) {
      console.error('Error creating case:', error);
      let errorMessage = 'Failed to create case';

      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else {
          errorMessage = 'Validation error occurred';
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConflictOverride = async () => {
    if (pendingCaseData) {
      setLoading(true);
      try {
        await createCaseAfterCheck(pendingCaseData);
      } catch (error) {
        console.error('Error creating case after override:', error);
        setError('Failed to create case');
      } finally {
        setLoading(false);
        setShowConflictDialog(false);
        setPendingCaseData(null);
      }
    }
  };

  const resetForm = () => {
    setCaseForm({
      case_number: '',
      title: '',
      case_type: '',
      description: '',
      client_id: preSelectedClientId || '',
      assigned_attorney_id: '',
      status: 'active',
      total_estimated_value: '',
      billing_type: 'hourly',
      flat_fee_amount: '',
      contingency_percentage: '',
      requires_trust_funds: true,
      trust_minimum_balance: '',
    });
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999] p-4"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      {/* Backdrop that doesn't cover navbar */}
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Briefcase className="h-6 w-6 mr-2 text-yellow-600" />
              Create New Case
            </h2>
            <p className="text-sm text-gray-600 mt-1">Create an empty case without using a template</p>
          </div>
          <button onClick={handleClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {loading && !error && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          )}

          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4 md:col-span-2">
                <h3 className="font-medium text-gray-900 text-lg border-b pb-2">Basic Information</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="case-title" className="block text-sm font-medium text-gray-700 mb-1">
                      Case Title *
                    </label>
                    <input
                      id="case-title"
                      type="text"
                      value={caseForm.title}
                      onChange={(e) => setCaseForm(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Enter case title"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Case Number
                    </label>
                    <input
                      type="text"
                      value={caseForm.case_number}
                      onChange={(e) => setCaseForm(prev => ({ ...prev, case_number: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Auto-generated if left blank"
                    />
                  </div>

                  <div>
                    <label htmlFor="case-type" className="block text-sm font-medium text-gray-700 mb-1">
                      Case Type *
                    </label>
                    <input
                      id="case-type"
                      type="text"
                      value={caseForm.case_type}
                      onChange={(e) => setCaseForm(prev => ({ ...prev, case_type: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="e.g., Personal Injury, Corporate"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="case-client" className="block text-sm font-medium text-gray-700 mb-1">
                      Client *
                    </label>
                    <AsyncSearchableSelect
                      value={caseForm.client_id}
                      onChange={(val) => setCaseForm(prev => ({ ...prev, client_id: val }))}
                      onSearch={searchClientsAsync}
                      emptyOption="Select a client"
                      required
                      disabled={!!preSelectedClientId}
                      selectedLabel={preSelectedClientName}
                    />
                    {preSelectedClientId && (
                      <p className="text-xs text-gray-500 mt-1">Client is pre-selected</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assigned Attorney
                    </label>
                    <SearchableSelect
                      value={caseForm.assigned_attorney_id}
                      onChange={(val) => setCaseForm(prev => ({ ...prev, assigned_attorney_id: val }))}
                      options={Array.isArray(attorneys) ? attorneys.map(attorney => ({
                        value: attorney.id,
                        label: `${attorney.first_name} ${attorney.last_name}`
                      })) : []}
                      emptyOption="Assign to me (default)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={caseForm.status}
                      onChange={(e) => setCaseForm(prev => ({
                        ...prev,
                        status: e.target.value,
                      }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                      <option value="active">Active</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>


                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={caseForm.description}
                      onChange={(e) => setCaseForm(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      rows="3"
                      placeholder="Optional case description"
                    />
                  </div>
                </div>
              </div>

              {/* Billing Information */}
              <div className="space-y-4 md:col-span-2">
                <h3 className="font-medium text-gray-900 text-lg border-b pb-2">Billing Configuration</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Type
                    </label>
                    <select
                      value={caseForm.billing_type}
                      onChange={(e) => setCaseForm(prev => ({ ...prev, billing_type: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                      <option value="hourly">Hourly</option>
                      <option value="flat_fee">Flat Fee</option>
                      <option value="contingency">Contingency</option>
                      <option value="retainer">Retainer</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Estimated Value ($)
                    </label>
                    <input
                      type="number"
                      value={caseForm.total_estimated_value}
                      onChange={(e) => setCaseForm(prev => ({ ...prev, total_estimated_value: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  {caseForm.billing_type === 'flat_fee' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Flat Fee Amount ($)
                      </label>
                      <input
                        type="number"
                        value={caseForm.flat_fee_amount}
                        onChange={(e) => setCaseForm(prev => ({ ...prev, flat_fee_amount: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="5000.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  )}

                  {caseForm.billing_type === 'contingency' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contingency Percentage (%)
                      </label>
                      <input
                        type="number"
                        value={caseForm.contingency_percentage}
                        onChange={(e) => setCaseForm(prev => ({ ...prev, contingency_percentage: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="33.33"
                        min="0"
                        max="100"
                        step="0.01"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Trust Fund Settings */}
              <div className="space-y-4 md:col-span-2">
                <h3 className="font-medium text-gray-900 text-lg border-b pb-2">Trust Fund Settings</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center md:col-span-2">
                    <input
                      type="checkbox"
                      id="requires-trust-funds"
                      checked={caseForm.requires_trust_funds}
                      onChange={(e) => setCaseForm(prev => ({ ...prev, requires_trust_funds: e.target.checked }))}
                      className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                    />
                    <label htmlFor="requires-trust-funds" className="ml-2 text-sm text-gray-700">
                      This case requires trust funds
                    </label>
                  </div>

                  {caseForm.requires_trust_funds && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Trust Minimum Balance ($)
                      </label>
                      <input
                        type="number"
                        value={caseForm.trust_minimum_balance}
                        onChange={(e) => setCaseForm(prev => ({ ...prev, trust_minimum_balance: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !caseForm.title || !caseForm.case_type || !caseForm.client_id}
              className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Create Case
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    <ConflictWarningDialog
      isOpen={showConflictDialog}
      onClose={() => {
        setShowConflictDialog(false);
        setPendingCaseData(null);
      }}
      onOverride={handleConflictOverride}
      conflicts={conflictData?.matches || []}
      conflictCheckId={conflictData?.conflictCheckId}
      loading={loading}
    />
    </>
  );
};

export default SimpleCaseModal;
